import axios, { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import { ITokens, IUserSSOPayload } from '../interfaces/User.interface';

const axiosAuthInstance = async (): Promise<AxiosInstance> => {
  let token = '';

  if (Cookies.get('token') !== null) {
    token = Cookies.get('token') as string;
  }

  const axiosApiInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL ?? 'https://lori-uat-api.oryxenergies.com',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      language: localStorage.getItem('OryxLang') !== null ? localStorage.getItem('OryxLang') : 'en',
    },
  });

  axiosApiInstance.interceptors.request.use((config) => {
    if (config.url !== undefined && Boolean(config.url.includes('/auth/silent'))) {
      config.headers['Authorization'] = `Bearer ${Cookies.get('refreshToken') as string}`;
      config.headers['Platform'] = 'web';
    } else if (config.url !== undefined && Boolean(config.url.includes('/auth/sso'))) {
      config.headers['x-api-key'] = process.env.REACT_APP_API_KEY ?? '8534e912-e3c1-44a4-8d3b-74d06021c11d';
    } else if (config.url !== undefined && Boolean(config.url.includes('/export'))) {
      config.responseType = 'blob';
    }

    return config;
  });

  axiosApiInstance.interceptors.response.use(
    async (response) => {
      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      const currentApiUrl: string | undefined = error?.response?.config?.url;

      if (!error.response) {
        console.error('Network/Server error:', error);
        throw error;
      }

      // Auth/login/sso failed → reject immediately
      if (
        status === 401 &&
        (currentApiUrl?.includes('/auth/login') || currentApiUrl?.includes('/auth/sso'))
      ) {
        throw error;
      }

      // Silent login failed → clear tokens
      if (error.response.status === 401 && currentApiUrl?.includes('/auth/silent')) {
        ClearTokenData();
        throw error;
      }

      // Generic 401 → try silent login once
      if (status === 401 && !currentApiUrl?.includes('/auth/silent')) {
        const data = await silentLogin();

        Cookies.set('token', data.accessToken, { secure: true });
        Cookies.set('refreshToken', data.refreshToken, { secure: true });

        // Ensure headers object exists
        if (error.config && error.config.headers) {
          error.config.headers['Authorization'] = `Bearer ${data.accessToken}`;
        }

        return await axiosApiInstance.request(error.config);
      }

      // Any other errors → just reject
      throw error;
    }
  );

  const ClearTokenData = (): void => {
    localStorage.clear();
    Cookies.remove('token');
    Cookies.remove('refreshToken');
    window.location.replace('/login');
  };

  return axiosApiInstance;
};

export { axiosAuthInstance };

export const createSSOUser = async (payload: IUserSSOPayload): Promise<ITokens> => {
  const client = await axiosAuthInstance();

  const response = await client.post('/auth/sso', payload);
  Cookies.set('token', response.data.accessToken, {
    secure: true,
  });
  Cookies.set('refreshToken', response.data.refreshToken, {
    secure: true,
  });
  return response.data;
};

const silentLogin = async (): Promise<ITokens> => {
  try {
    const client = await axiosAuthInstance();
    const response = await client.post('/auth/silent');
    return response.data;
  } catch (error) {
    return await Promise.reject(error);
  }
};
